import Modal from '@/components/Modal';
import React, { FC, useRef, useState } from 'react';
import Tabs from '@/components/DesignLibrary/Tabs';
import Spacer from '@/components/Spacer';
import Upload from '@/components/Icons/Upload';
import ArrowCircleLeft from '@/components/Icons/ArrowCircleLeft';
import Button from '@/components/DesignLibrary/Button';
import DocumentUploader, { DocumentUploaderRef } from '@/components/Document/DocumentUploader';
import NoodleAI from '@/components/Icons/NoodleAI';
import { PAY_ORGANIZER_KEY_TO_LABEL, TABLE_COLUMNS } from '@lib/DocumentRequestUserDetailV3/constants';
import Label from '@components/DesignLibrary/Atoms/shared/Label';
import * as tsClient from '@tsClient';
import useNoodleApi from '@hooks/useNoodleApi';
import Bluebird from 'bluebird';

import SizeErrorModal from '../UploadBankStatementModal/SizeErrorModal';
import s from './AddPayOrganizerItem.module.scss';

const TABS = [
  { id: 'pdf', label: 'Upload PDF' },
  { id: 'manual', label: 'Manually' },
];

type Props = {
  onClose: () => void;
  onSave: () => Promise<void>;
  userFileId: string;
  documentRequestUserId: string;
};

const AddPayOrganizerItem: FC<Props> = ({ onClose, onSave, userFileId, documentRequestUserId }) => {
  const [selectedTab, setSelectedTab] = useState(TABS[0].id);
  const [selectedPage, setSelectedPage] = useState<'upload' | 'processing' | 'inputs'>('upload');
  const [isUploading, setIsUploading] = useState(false);
  const [isSizeErrorModalOpen, setIsSizeErrorModalOpen] = useState(false);
  const documentUploaderRef = useRef<DocumentUploaderRef>(null);

  const { getData: addUserFileDocumentFn } = useNoodleApi(tsClient.documentRequests.createUserFileDocument, { toastOnError: true });

  return (
    <>
      <Modal
        title="Add item"
        onClose={selectedPage === 'processing' || selectedPage === 'inputs' ? () => setSelectedPage('upload') : onClose}
        closeIcon={selectedPage === 'processing' || selectedPage === 'inputs' ? <ArrowCircleLeft weight="fill" size={24} color="var(--color-gray-100)" /> : undefined}
      >
        <Spacer size={8} />
        {selectedPage !== 'processing' && selectedPage !== 'inputs' && (
          <Tabs inputName="tabs" onChange={id => setSelectedTab(id)} checked={selectedTab} inputs={TABS} />
        )}
        <div className={s.container}>
          {selectedTab === 'pdf' && (
            <>
              {selectedPage === 'upload' && (
                <div className={s.fileSelection}>
                  <Upload size={24} weight="fill" />
                  <Button variant="secondary" size="sm" loading={isUploading} onClick={() => documentUploaderRef.current?.uploadDocument()}>
                    Upload a PDF
                  </Button>
                  <DocumentUploader
                    isMultiple
                    ref={documentUploaderRef}
                    documentRequestUserFileId={userFileId}
                    onUploadProgressChange={() => {}}
                    onUploadError={() => {}}
                    onFinishUpload={async () => {
                      await onSave();
                      await Bluebird.delay(1000);
                      onClose();
                    }}
                    isSizeError={setIsSizeErrorModalOpen}
                    onUploadStart={() => {
                      setIsUploading(true);
                      setSelectedPage('processing'); // should be on pdf processing complete
                    }}
                  />
                  <div className={s.aiSection}>
                    <NoodleAI size={12} /> Glade Ai will extract data from the uploaded PDF for your review.
                  </div>
                </div>
              )}
              {selectedPage === 'processing' && (
                <div className={s.fileSelection}>
                  <NoodleAI size={24} />
                  <p className="caption">Processing with Glade.ai...</p>
                </div>
              )}
            </>
          )}
          {(selectedTab === 'manual' || selectedPage === 'inputs') && (
            <form className={s.inputs} onSubmit={async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
              e.preventDefault();
              setIsUploading(true);
              const formData = new FormData(e.currentTarget as HTMLFormElement);
              await addUserFileDocumentFn({
                fileId: userFileId,
                id: documentRequestUserId,
                metadata: {
                  tableData: {
                    ...Object.fromEntries(Array.from(formData.entries()).map(([key, value]) => [key, key === 'payDate' ? value : Number(value)])),
                  },
                  useInCalculation: true,
                  useInMeansTest: true,
                },
              });
              await Bluebird.delay(1000);
              await onSave();
              onClose();
            }}>
              <div>
                <Spacer />
                {Object.entries(PAY_ORGANIZER_KEY_TO_LABEL)
                  .filter(([, label]) => label !== 'Net Pay')
                  .sort(([, labelA], [, labelB]) => {
                    const indexA = TABLE_COLUMNS.findIndex(col => col === labelA);
                    const indexB = TABLE_COLUMNS.findIndex(col => col === labelB);
                    return indexA - indexB;
                  })
                  .map(([key, label]) => (
                    <Label key={key} label={key === 'payDate' ? label : `${label} (USD)`} compact>
                      <input
                        className={s.payTableInput}
                        type={key === 'payDate' ? 'date' : 'string'}
                        name={key}
                        id={key}
                        onClick={(e) => e.currentTarget.select()}
                      />
                    </Label>
                  ))
                }
              </div>
              <Button variant="primary" size="md" fullWidth type='submit' loading={isUploading}>
                Save
              </Button>
            </form>
          )}
        </div>
      </Modal>
      {isSizeErrorModalOpen && <SizeErrorModal setInvalidDocModalOpen={setIsSizeErrorModalOpen} />}
    </>
  );
};

// eslint-disable-next-line import/no-unused-modules
export default AddPayOrganizerItem;
